import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import flow from '../../services/flow'
import { isSwedenPortal } from '../../utils/portalCountry'

const CUSTOMER_DECLARATION_FLOW_DEFINITION_ID = isSwedenPortal
  ? 'customer-declaration-private-new-se'
  : 'customer-declaration-private-new'
const CUSTOMER_DECLARATION_BUSINESS_FLOW_DEFINITION_ID = isSwedenPortal
  ? 'customer-declaration-business-new-se'
  : 'customer-declaration-business-new'

const statuses = {
  'awaiting-customer-answer': {
    label: 'Pending',
    style: 'bg-yellow-100 text-yellow-800'
  },
  'awaiting-credit-approval': {
    label: 'Awaiting credit approval',
    style: 'bg-yellow-100 text-yellow-800'
  },
  assessment: {
    label: 'Answered',
    style: 'bg-blue-100 text-blue-800'
  },
  completed: {
    label: 'Completed',
    style: 'bg-green-100 text-green-800'
  }
}

function CustomerDeclarations({
  referenceId,
  contractStatus,
  isPrivateCustomer
}: any) {
  const history = useHistory()

  const { data: flows = [], refetch } = useQuery({
    queryKey: ['kyc', referenceId],
    queryFn: async () =>
      referenceId
        ? await flow.fetchFlows(
            referenceId,
            isPrivateCustomer
              ? CUSTOMER_DECLARATION_FLOW_DEFINITION_ID
              : CUSTOMER_DECLARATION_BUSINESS_FLOW_DEFINITION_ID
          )
        : [],
    enabled: !!referenceId,
    staleTime: 300000
  })

  useEffect(() => {
    refetch()
  }, [refetch])

  const getStatus = (flow: any) => {
    if (['Approved', 'Finalized'].includes(contractStatus)) {
      return statuses.completed
    }

    const status = flow.data.businessStatus
    return {
      label: statuses[status]?.label || status,
      style: statuses[status]?.style || 'bg-gray-100 text-gray-800'
    }
  }

  const handleViewCase = (definitionId: string, flowId: string) => {
    history.push(`/cases/case/${definitionId}/${flowId}`)
  }

  const hasFlows = flows.length > 0
  if (!hasFlows) {
    return null
  }

  return (
    <div className="mt-4 px-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">
        Customer Declarations
      </h3>
      <div className="space-y-2">
        {flows
          .sort(
            (a, b) =>
              b.data.customer.isMainApplicant - a.data.customer.isMainApplicant
          )
          .map((flow) => {
            const status = getStatus(flow)
            return (
              <div
                key={flow.flowId}
                className="flex flex-col md:flex-row md:items-center justify-between bg-gray-50 p-3 rounded-md border gap-2"
              >
                <div className="text-sm text-gray-900">
                  {flow.data.customer.name} ({flow.data.customer.id})
                  {isPrivateCustomer && !flow.data.customer.isMainApplicant && (
                    <span className="text-xs text-gray-500 ms-1">
                      (Co-applicant)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${status.style}`}
                  >
                    {status.label}
                  </span>
                  <button
                    onClick={() =>
                      handleViewCase(flow.flowDefinitionId, flow.flowId)
                    }
                    className="text-xs text-blue-600 underline"
                  >
                    View case
                  </button>
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default CustomerDeclarations
